<template>
  <span>
    <div
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
      class="checkbox"
      :class="{ 'checkbox--disabled': disabled }"
      :tabindex="tabindex"
      ref="checkbox"
    >
      <div class="checkbox__box" :class="{ 'checkbox--is-true': value }">
        <div class="checkbox__hitbox"></div>
        <font-awesome-icon class="checkbox__icon" :icon="selectionIcon" />
      </div>
      <label
        class="checkbox__label"
        :class="{ 'checkbox__label--disabled': disabled }"
        @click.stop="labelClickHandler"
        v-html="label"
      ></label>
    </div>
    <ValidationMessage
      class="checkbox__validation-message"
      :display-invalid="displayInvalid"
      :validation-message="stockValidationMessage"
    />
  </span>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheck, faMinus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import BaseComponent from "../../BaseComponent";
import InteractiveElement from "../../mixins/InteractiveElements";
import InputBase from "./mixins/InputBase";

library.add(faCheck);
library.add(faMinus);

/**
 * @group Inputs
 */
export default {
  mixins: [BaseComponent, InputBase, InteractiveElement],
  name: "Checkbox",
  props: {
    // Text that appears next to the Checkbox
    label: {
      type: String,
    },
    // If Checkbox is not checked, and the user submits, this text is shown
    validationMessage: {
      type: String,
    },
    // Defines if Checkbox has to be toggled on submit
    required: {
      type: Boolean,
      default: false,
    },
    // Overwrite the default tab-index of 0
    tabindex: {
      type: Number,
      default: 0,
    },
    enableExternalValidation: {
      type: Boolean,
      default: false,
    },
    externalValidationError: {
      type: Boolean,
    },
    externalValidationErrorMessage: {
      type: String,
    },
    partiallySelected: {
      type: Boolean,
    },
  },
  components: {
    FontAwesomeIcon,
  },
  computed: {
    displayInvalid() {
      if (this.enableExternalValidation) return this.externalValidationError;
      return this.isShowingValidationMessage && !this.isValid;
    },
    stockValidationMessage() {
      if (this.enableExternalValidation)
        return this.externalValidationErrorMessage;
      let stockValidationMessage = "This field is required";
      if (this.validationMessage)
        stockValidationMessage = this.validationMessage;
      return stockValidationMessage;
    },
    isValid() {
      return (this.required && this.value !== false) || !this.required;
    },
    selectionIcon() {
      return this.partiallySelected ? ["far", "minus"] : ["far", "check"];
    },
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      // Toggles Checkbox checked
      if (this.value === true && !this.partiallySelected) {
        this.setFalse();
      } else {
        this.setTrue();
      }
      this.$emit("click", this.inputValue);
    },
    labelClickHandler(event) {
      if (this.disabled) return;
      const path = event.path.slice(0, event.path.indexOf(this.$refs.checkbox));
      const anchorsInPath = path.find((element) => element.nodeName === "A");
      const preventCheckBoxToggle = anchorsInPath !== undefined;
      if (!preventCheckBoxToggle) this.toggle();
    },
    setTrue() {
      // Set Checkbox to checked
      this.inputValue = true;
    },
    setFalse() {
      // Set Checkbox to unchecked
      this.inputValue = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../../style_variables/style_variables.scss";
.checkbox {
  display: inline-flex;
  position: relative;
  &:hover {
    cursor: pointer;
    .checkbox__box {
      background-color: $grey_athens;
    }
  }
  &.checkbox--disabled {
    .checkbox__box {
      background-color: $grey_bg;
      border: 2px solid $grey_mischka;
      &.checkbox--is-true {
        background-color: $grey_mischka !important;
        border-color: $grey_mischka;
      }
    }
    .checkbox__label {
      cursor: default;
    }
    &:hover {
      cursor: default;
      .checkbox__box {
        background-color: $grey_bg;
      }
    }
  }
}
.checkbox__box {
  position: relative;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 4px;
  background-color: $white;
  border: 2px solid $grey_french;
  transition: 220ms ease-out;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  &:hover {
    .checkbox__thumb-background {
      transform: scale(1);
    }
  }
  &.checkbox--is-true {
    background-color: $blue !important;
    border-color: $blue;
    .checkbox__icon {
      opacity: 1;
    }
  }
}
.checkbox__icon {
  opacity: 0;
  font-size: 14px;
  color: $white;
}
.checkbox__hitbox {
  position: absolute;
  z-index: 2;
  width: 80px;
  height: 41px;
  margin: -6px -10px;
}
.checkbox__rail {
  width: 100%;
  height: 27px;
}
.checkbox__label {
  @extend %body2_style;
  margin-top: -1px;
  padding-left: 8px;
  &:hover {
    cursor: pointer;
  }
  &.checkbox__label--disabled {
    color: $grey_mischka;
  }
}
.checkbox__validation-message {
  display: block;
  width: 100%;
  margin-top: 8px;
}
</style>
